import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Printer, ArrowLeft, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { fmtDateTime } from "@/utils/datetime";

function formatMoney(minor: number, currency: string, lang: string) {
    return new Intl.NumberFormat(lang, { style: "currency", currency }).format(minor / 100);
}

export default function Receipt() {
    const { t, i18n } = useTranslation();
    const { id } = useParams();
    const [data, setData] = useState<any>(null);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        (async () => {
            try { const { data } = await api.get(`/portal/finance/receipt/${id}`); setData(data); }
            catch (e: any) { setErr(formatApiError(e)); }
        })();
    }, [id]);

    if (err) return <div className="p-6 text-sm" style={{ color: "var(--error-fg)" }} data-testid="receipt-error">{err}</div>;
    if (!data) return <div className="flex justify-center p-8"><Loader2 className="animate-spin" size={22} /></div>;

    const { payment, person, tenant, allocations } = data;
    return (
        <div className="min-h-screen py-6 print:py-0" style={{ background: "var(--page)", color: "var(--text)" }}>
            <div className="max-w-2xl mx-auto px-4 sm:px-6" data-testid="portal-receipt">
                <div className="flex items-center justify-between mb-4 print:hidden">
                    <Link to="/portal" className="inline-flex items-center gap-1 text-sm" style={{ color: "var(--text-muted)" }} data-testid="receipt-back"><ArrowLeft size={16} />{t("common.back")}</Link>
                    <button data-testid="receipt-print" onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium" style={{ background: "var(--primary)", color: "var(--primary-fg)" }}><Printer size={16} />{t("finance.print")}</button>
                </div>
                <div className="rounded-xl border p-6 print:border-0 print:p-0" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
                    <div className="flex items-start justify-between border-b pb-4 mb-4" style={{ borderColor: "var(--border)" }}>
                        <div>
                            <div className="text-lg font-semibold">{tenant.legal_name || tenant.name}</div>
                            {tenant.tax_id && <div className="text-xs" style={{ color: "var(--text-muted)" }}>VÖEN: {tenant.tax_id}</div>}
                            <div className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>{[tenant.contact_email, tenant.contact_phone].filter(Boolean).join(" · ")}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-xs" style={{ color: "var(--text-muted)" }}>{t("finance.receipt")}</div>
                            <div className="font-mono text-sm">{payment.receipt_number}</div>
                        </div>
                    </div>
                    <dl className="grid grid-cols-2 gap-3 text-sm">
                        <div><dt className="text-xs" style={{ color: "var(--text-muted)" }}>{t("finance.invoice.person")}</dt><dd>{person.first_name} {person.last_name}</dd></div>
                        <div><dt className="text-xs" style={{ color: "var(--text-muted)" }}>{t("finance.paid_at")}</dt><dd>{fmtDateTime(payment.paid_at)}</dd></div>
                        <div><dt className="text-xs" style={{ color: "var(--text-muted)" }}>{t("finance.method")}</dt><dd>{t(`finance.method.${payment.method}`)}</dd></div>
                        <div><dt className="text-xs" style={{ color: "var(--text-muted)" }}>{t("common.status")}</dt><dd>{t(`finance.payment.status.${payment.status}`)}</dd></div>
                    </dl>
                    <div className="mt-5 border-t pt-4" style={{ borderColor: "var(--border)" }}>
                        <div className="text-xs mb-2" style={{ color: "var(--text-muted)" }}>{t("finance.payment.allocations")}</div>
                        {allocations.length === 0 && <div className="text-sm" style={{ color: "var(--text-muted)" }}>—</div>}
                        {allocations.map((a: any, idx: number) => (
                            <div key={idx} className="flex justify-between text-sm py-1">
                                <span>{a.invoice_number || a.invoice_id.slice(0, 8)}</span>
                                <span>{formatMoney(a.amount_minor, payment.currency, i18n.language)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-4 flex items-center justify-between text-lg font-semibold border-t pt-3" style={{ borderColor: "var(--border)" }}>
                        <span>{t("finance.total")}</span>
                        <span data-testid="receipt-total">{formatMoney(payment.amount_minor, payment.currency, i18n.language)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}
